import { validate_id } from "@/utils/validations";
import { MAIN_CONTROLLER } from "@/utils/main_controller.js";
import { PREPOPULATE_INPUT } from "@/utils/prepopulate_input.js";
import BreadCrumb from "@/components/BreadCrumb/BreadCrumb.vue";
import ClientLogo from "@/components/ClientLogo/ClientLogo.vue";
import { API_BASE_URL } from "@/config";
import { validateToken } from "@/utils/auth";
import LivenessCheck from "@/components/Verification/liveness_check/LivenessCheck.vue";

export default {
  name: "FacialBiometricsVerification",
  mixins: [MAIN_CONTROLLER, PREPOPULATE_INPUT],
  components: {
    BreadCrumb,
    ClientLogo,
    LivenessCheck,
  },
  data() {
    return {
      id_number: "",
      error_message: "",
      showInstructions: false,
      showLiveness: false,
      loading: false,
      dataLoaded: false,
      hasPrepopulatedNames: false,
    };
  },
  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },
    clientLogo() {
      const domain = window.location.hostname;
      let logoPath;
      if (domain.includes("wizzit")) {
        logoPath = require("@/assets/images/Wizzit-Logo-Red.svg");
      } else if (domain.includes("vodacom")) {
        logoPath = require("@/assets/images/new_voda.svg");
      } else {
        logoPath = require("@/assets/images/new_black_logo2.svg");
      }
      return logoPath;
    },
    showNameInputs() {
      return this.dataLoaded && !this.hasPrepopulatedNames;
    },
    namesAreValid() {
      return (
        this.first_name &&
        this.last_name &&
        this.first_name.trim().length > 0 &&
        this.last_name.trim().length > 0
      );
    },
    isFormValid() {
      const idValid = this.id_number && this.validate_id(this.id_number);
      return idValid && this.namesAreValid;
    },
  },
  methods: {
    validate_id,
    handle_input() {
      this.error_message = "";
    },
    closeInstructions() {
      this.showInstructions = false;
    },
    async get_customer_data() {
      const TOKEN = localStorage.getItem("token");
      const CUSTOMER_DATA_URL = `${API_BASE_URL}/verify_api/system_management_api/get_customer_data/`;

      try {
        await $.ajax({
          url: CUSTOMER_DATA_URL,
          method: "GET",
          contentType: "application/json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          beforeSend: () => {
            Swal.fire({
              text: "Loading ...",
              allowEscapeKey: false,
              allowOutsideClick: false,
              didOpen: () => Swal.showLoading(),
            });
          },
          success: (response) => {
            Swal.close();
            this.form_data = { ...this.form_data, ...response };
            this.sync_form_data();

            const hasFirstName =
              response.first_name && response.first_name.trim().length > 0;
            const hasLastName =
              response.last_name && response.last_name.trim().length > 0;
            this.hasPrepopulatedNames = hasFirstName && hasLastName;
          },
        });
      } catch (error) {
        Swal.close();
        console.error("Error fetching customer data:", error);
        this.hasPrepopulatedNames = false;
      }
    },

    async id_check() {
      if (!this.namesAreValid) {
        this.error_message = "Please enter both first and last name";
        return;
      }
      const TOKEN = localStorage.getItem("token");
      const ID_NUMBER = this.id_number;
      const PREPOPULATED_URL = `${API_BASE_URL}/verify_api/personnel_verification/check_id_number_exists/?id_number=${ID_NUMBER}&workflow_id=${this.workflow_id}`;

      this.error_message = "";
      this.loading = true;
      Swal.fire({
        text: "Checking ID...",
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await $.ajax({
          url: PREPOPULATED_URL,
          method: "GET",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
        });

        if (response.exists) {
          this.error_message = "ID number already registered.";
        } else {
          this.error_message = "";
          this.showInstructions = true;
        }
      } catch (error) {
        this.error_message = "Error verifying ID. Try again.";
      } finally {
        this.loading = false;
        Swal.close();
      }
    },
    startFaceRecognition() {
      this.showInstructions = false;
      this.showCamera = true;
      this.$refs.livenessCheck.unlockAudio();
      this.$refs.livenessCheck.startFaceRecognition();
    },
    async handlePhotoCaptured(photo_src) {
      await this.safps_verification(photo_src);
    },
    async safps_verification(photo_src) {
      const TOKEN = localStorage.getItem("token");
      const SAFPS_URL = `${API_BASE_URL}/verify_api/personnel_verification/customer_onboarding_liveness_verification/`;
      const DATA = {
        on_behalf_application:
          this.$route.name === "behalf_application" ? true : false,
        id_number: this.id_number,
        first_name: (this.first_name || "").trim(),
        last_name: (this.last_name || "").trim(),
        biometric: photo_src,
        workflow_id: this.workflow_id,
      };
      Swal.fire({
        text: "Loading ...",
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      $.ajax({
        url: SAFPS_URL,
        method: "POST",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        data: JSON.stringify(DATA),
        success: (response) => {
          Swal.close();
          const status_message = response.status;
          var liveness_value = response.liveness;
          const message = response.message;
          var redirect = response.redirect;
          const user_role = response["role"];

          if (redirect === "alternative_liveness_verification") {
            Swal.fire({
              icon: "info",
              title: "Service Unavailable",
              text: "The current service is unavailable. Switching to an alternative service.",
              allowOutsideClick: false,
            }).then(() => {
              this.$router.push(`/alternative_liveness_verification`);
            });
            return;
          }

          if (status_message === "success") {
            if (user_role === "Director") {
              this.$refs.livenessCheck?.close_modal();
              this.$router.push("/upload_proof_of_address");
            } else {
              this.$refs.livenessCheck?.close_modal();
              this.fetch_current_service();
            }
          } else if (
            status_message === "error" &&
            response.result === "Results not found. Contact Provider"
          ) {
            this.$refs.livenessCheck?.close_modal();
            this.fetch_current_service();
          }
          if (liveness_value === "User is wearing a hat") {
            liveness_value = "Please remove your hat and try again";
          }
          if (liveness_value === "User is wearing glasses") {
            liveness_value = "Please remove your glasses and try again";
          }

          if (liveness_value) {
            Swal.fire({
              icon: "error",
              title: liveness_value,
            }).then(() => {
              location.reload();
            });
          }

          if (message) {
            Swal.fire({
              icon: "error",
              title: message,
            }).then(() => {
              location.reload();
            });
          }
        },
        error: (error) => {
          Swal.close();
          let error_message = "Error retrieving data";
          if (error.responseJSON) {
            error_message =
              error.responseJSON.id_number?.[0] ||
              error.responseJSON.first_name?.[0] ||
              error.responseJSON.last_name?.[0] ||
              error_message;
          }
          this.error_message = error_message;
        },
      });
    },
  },
  async mounted() {
    if (!validateToken(this.$router)) return;

    try {
      await this.get_customer_data();
    } catch (error) {
      console.error("Error fetching customer data:", error);
    } finally {
      this.dataLoaded = true;
    }
  },
};
